import React, { useEffect, useState } from 'react';
import { validateEmailFormat } from '../patient/PatientInfoForm';
import { handoffToPatient, logEvent } from '../util/apiUtils';

/**
 * Ask for the patient's email address and send an activation reminder handoff email.
 *
 * @param gearOrder
 * @param onSuccess Function to notify the parent of a success
 * @param onCancel Function to notify the parent when 'Cancel' is clicked
 * @param onLoading Function to notify the parent when an email request is in progress
 */
const SendActivationEmail = ({ gearOrder, onSuccess, onCancel, onLoading }) => {
	const [email, setEmail] = useState(gearOrder?.email || '');
	const [emailError, setEmailError] = useState(null);
	const [disabled, setDisabled] = useState(false);
	const [validateInputs, setValidateInputs] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [success, setSuccess] = useState(false);

	useEffect(() => {
		let emailError = null;
		if (!email) {
			emailError = 'Please provide an email address';
		} else if (!validateEmailFormat(email)) {
			emailError = 'Please provide a valid email address';
		}

		setEmailError(emailError);
		setDisabled(validateInputs && !!emailError);
	}, [email, validateInputs]);

	const handleEmailChange = (event) => {
		setEmail(event?.target?.value);
	};

	/**
	 * Set the loading value in state and then pass the value to the parent through onLoading
	 */
	const handleLoading = (loading) => {
		setLoading(loading);
		onLoading && onLoading(loading);
	};

	const handleSubmit = (event) => {
		setError(false);
		setSuccess(false);

		// Turn on validation error messages once the submit button is clicked
		setValidateInputs(true);
		if (!email || !validateEmailFormat(email)) {
			setEmailError('Please provide a valid email address');
			setDisabled(true);
			return;
		}

		handleLoading(true);

		let patientInfo = gearOrder.patientInfo || {};
		patientInfo.email = email;

		// Send the email
		handoffToPatient(gearOrder.id, patientInfo, 'activate')
			.then((gearOrder) => {
				setSuccess(true);
				onSuccess(gearOrder);
			})
			.catch((e) => {
				setError(true);
			})
			.finally(() => {
				handleLoading(false);
				logEvent(
					'{} - Activation Email Sent. gearOrderId={}, email={}',
					'UI_ACTION_SEND_ACTIVATION_EMAIL',
					gearOrder.id,
					email
				);
			});
	};

	return (
		<div>
			<p>
				<strong>
					We'll send an email with the activation number and FAQs to help your patient return their specimen
					collection.
				</strong>
			</p>
			<div>
				<div className={validateInputs && !!emailError ? ' has-error' : ''}>
					<input
						className="form-control"
						id="patient-email"
						maxLength="100"
						type="text"
						defaultValue={email}
						onChange={handleEmailChange}
						data-fieldname="email"
						placeholder="patient@example.com"
					></input>
					{validateInputs && !!emailError && (
						<span className="validation-message text-danger">{emailError}</span>
					)}
				</div>
				<p className="mt-5 small">
					<i>The address will be used to send activation information. Please ensure it is accurate.</i>
				</p>
				{error && (
					<div className="text-danger mb-5">
						<i className="fa fa-times-circle" style={{ margin: '1em', float: 'left' }}></i>
						<span>
							The email failed to send. Please verify the email address is correct.
							<br />
							If you need assistance please call us at 800-522-4762.
						</span>
					</div>
				)}
				{success && (
					<span className="text-success">
						<i className="fa fa-check-circle mr-5"></i>An email has been sent to your patient.
					</span>
				)}
				<div className="text-right">
					{loading && (
						<span className="text-info" style={{ padding: '9px', display: 'inline-block' }}>
							Sending... <i className="fa fa-spinner fa-spin fa-fw"></i>
						</span>
					)}
					{!loading && (
						<>
							<button
								className="btn btn-white radius btn-sm"
								style={{
									margin: '0',
									marginTop: '5px',
									marginRight: '20px',
									borderRadius: '25px',
									paddingRight: '25px',
									paddingLeft: '25px'
								}}
								onClick={onCancel}
							>
								Cancel
							</button>
							<button
								className="btn btn-primary radius btn-sm"
								style={{
									margin: '0',
									marginTop: '5px',
									borderRadius: '25px',
									paddingRight: '25px',
									paddingLeft: '25px'
								}}
								onClick={handleSubmit}
								disabled={disabled}
								title={disabled ? 'Please provide an email address' : ''}
							>
								Send Email
							</button>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default SendActivationEmail;
